import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdSearch, MdFavorite } from 'react-icons/md';
import { FiSettings, FiLock, FiUnlock, FiClock, FiRefreshCw } from 'react-icons/fi';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import './TopNavbar.css';

interface NavIconProps {
  icon: React.ReactNode;
  label: string;
  focused?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  onClick?: () => void;
}

type NavIconItem = NavIconProps & { actionIndex: number };

const NavIconButton: React.FC<NavIconProps> = ({ icon, label, focused, disabled, hidden, onClick }) => (
  <button
    type="button"
    className={`topnav__icon-btn${focused ? ' topnav__icon-btn--focused' : ''}${disabled ? ' topnav__icon-btn--disabled' : ''}${hidden ? ' topnav__icon-btn--hidden' : ''}`}
    aria-label={label}
    onClick={disabled || hidden ? undefined : onClick}
    tabIndex={-1}
    aria-disabled={disabled}
    aria-hidden={hidden}
  >
    <div className="topnav__icon-circle">{icon}</div>
    <span className="topnav__icon-label">{label}</span>
  </button>
);

export const NAV_ICON_SEARCH_INDEX = 0;
export const NAV_ICON_FAVORITES_INDEX = 1;
export const NAV_ICON_SETTINGS_INDEX = 2;
export const NAV_ICON_LOCK_INDEX = 3;
export const NAV_ICON_HISTORY_INDEX = 4;
export const NAV_ICON_REFRESH_INDEX = 5;

export const TopNavbar: React.FC<{ focusedIndex?: number; hideParentalControl?: boolean; onIconClick?: (index: number) => void }> = ({ focusedIndex, hideParentalControl = false, onIconClick }) => {
  const { t } = useTranslation();
  const { isEnabled, isKidsMode } = useParentalControls();
  const navIcons: NavIconItem[] = [
    { actionIndex: NAV_ICON_SEARCH_INDEX, icon: <MdSearch size={22} />, label: t('nav.search'), disabled: isKidsMode, hidden: isKidsMode },
    { actionIndex: NAV_ICON_FAVORITES_INDEX, icon: <MdFavorite size={20} />, label: t('common.favorites') },
    { actionIndex: NAV_ICON_SETTINGS_INDEX, icon: <FiSettings size={20} />, label: t('settings.title') },
    {
      actionIndex: NAV_ICON_LOCK_INDEX,
      icon: isEnabled ? <FiLock size={18} /> : <FiUnlock size={18} />,
      label: isEnabled ? t('parental.unlockLabel') : t('parental.lockLabel'),
    },
    { actionIndex: NAV_ICON_HISTORY_INDEX, icon: <FiClock size={18} />, label: t('nav.history') },
    { actionIndex: NAV_ICON_REFRESH_INDEX, icon: <FiRefreshCw size={18} />, label: t('catalogUpdate.title', 'Atualizar conteúdo') },
  ];
  const visibleNavIcons = hideParentalControl
    ? navIcons.filter((item) => item.actionIndex !== NAV_ICON_LOCK_INDEX)
    : navIcons;
  const [time, setTime] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');
  const timeStr = `${pad(time.getHours())}:${pad(time.getMinutes())}`;

  return (
    <header className="topnav">
      {/* Logo */}
      <div className="topnav__logo">
        <span className="topnav__logo-name"></span>
      </div>

      {/* Spacer */}
      <div className="topnav__spacer" />

      {/* Action icons */}
      <nav className="topnav__actions" aria-label={t('nav.navbar')}>
        {visibleNavIcons.map((item, i) => (
          <NavIconButton
            key={item.actionIndex}
            icon={item.icon}
            label={item.label}
            focused={focusedIndex === i}
            disabled={item.disabled}
            hidden={item.hidden}
            onClick={() => onIconClick?.(item.actionIndex)}
          />
        ))}
      </nav>

      {/* Clock */}
      <div className="topnav__clock" aria-live="polite" aria-atomic="true">
        {timeStr}
      </div>
    </header>
  );
};
